// ===== Packages =====

import {
    fetchSignInMethodsForEmail,
}                               from 'firebase/auth';

// ===== Firebase =====

import fire                     from '../fire';

// ===== Enums =====

import {
    AUTHENTICATION_ERROR_CODE,
    AUTHENTICATION_TYPE,
}                               from '../enums';

// ===== Services =====

import {
    getAuthErrorMessage,
    getAuthenticationTypeLabel,
    getSignInMethodLabel,
    storageTotallyBlocked,
    formatProviderList,
}                               from '.';

const { auth } = fire();

const STORAGE_NOT_ALLOWED_ERROR = (
    'Can\'t sign you in because local storage is disabled. '
    + 'Please open website in regular (not Private) window and allow cookies.'
);
const PROVIDER_SWITCH_ERROR_CODES = new Set<string>([
    AUTHENTICATION_ERROR_CODE.accountExistsWithDifferentCredential,
    AUTHENTICATION_ERROR_CODE.credentialAlreadyInUse,
]);

const getProviderSignInBaseMessage = (type: AUTHENTICATION_TYPE): string => {
    switch (type) {
    case AUTHENTICATION_TYPE.google:
        return 'There was a problem signing in with Google';
    case AUTHENTICATION_TYPE.github:
        return 'There was a problem signing in with Github';
    case AUTHENTICATION_TYPE.facebook:
        return 'There was a problem signing in with Facebook';
    case AUTHENTICATION_TYPE.xTwitter:
        return 'There was a problem signing in with X/Twitter';
    default:
        return 'There was a problem signing in';
    }
};

const getProviderSignInLabel = (type: AUTHENTICATION_TYPE): string => getAuthenticationTypeLabel(type);

const getAlternativeProviderGuidance = (type: AUTHENTICATION_TYPE): string => {
    const providerOptions = [
        AUTHENTICATION_TYPE.google,
        AUTHENTICATION_TYPE.github,
        AUTHENTICATION_TYPE.facebook,
        AUTHENTICATION_TYPE.xTwitter,
    ]
        .filter((providerType) => providerType !== type)
        .map((providerType) => getProviderSignInLabel(providerType));

    const providerOptionsText = providerOptions.length > 1
        ? `${providerOptions.slice(0, -1).join(', ')}, or ${providerOptions[providerOptions.length - 1]}`
        : providerOptions[0];

    return `Try signing in with ${providerOptionsText}.`;
};

const getProviderSwitchFallbackMessage = (type: AUTHENTICATION_TYPE): string => {
    const signInBaseMessage = getProviderSignInBaseMessage(type);
    const providerSignInLabel = getProviderSignInLabel(type);
    return (
        `${signInBaseMessage}: This email is already linked to a different sign-in method `
        + `than ${providerSignInLabel}. ${getAlternativeProviderGuidance(type)}`
    );
};

const getErrorCustomDataEmail = (customData?: Record<string, unknown>): string | undefined => {
    const email = customData?.email;
    if (typeof email !== 'string') return undefined;
    const trimmedEmail = email.trim();
    return trimmedEmail.length > 0 ? trimmedEmail : undefined;
};

const getProviderAuthErrorMessage = ({
    type,
    error,
}: {
    type: AUTHENTICATION_TYPE,
    error: {
        code?: string,
        message?: string,
        customData?: Record<string, unknown>,
    },
}): string => {
    const signInBaseMessage = getProviderSignInBaseMessage(type);
    if (storageTotallyBlocked()) {
        return `${signInBaseMessage}: ${STORAGE_NOT_ALLOWED_ERROR}`;
    }
    if (error.code && PROVIDER_SWITCH_ERROR_CODES.has(error.code)) {
        return getProviderSwitchFallbackMessage(type);
    }
    return getAuthErrorMessage(error.code as AUTHENTICATION_ERROR_CODE)
        || `${signInBaseMessage}: ${error.message || 'Unknown authentication error.'}`;
};

export const resolveProviderAuthErrorMessage = async ({
    type,
    error,
}: {
    type: AUTHENTICATION_TYPE,
    error: {
        code?: string,
        message?: string,
        customData?: Record<string, unknown>,
    },
}): Promise<string> => {
    const signInBaseMessage = getProviderSignInBaseMessage(type);
    if (storageTotallyBlocked()) {
        return `${signInBaseMessage}: ${STORAGE_NOT_ALLOWED_ERROR}`;
    }

    if (error.code && PROVIDER_SWITCH_ERROR_CODES.has(error.code)) {
        const customDataEmail = getErrorCustomDataEmail(error.customData);
        if (!customDataEmail) {
            return getProviderSwitchFallbackMessage(type);
        }

        try {
            const methods = await fetchSignInMethodsForEmail(auth, customDataEmail);
            const providerLabels = Array.from(
                new Set(methods.map((method) => getSignInMethodLabel(method))),
            );
            if (providerLabels.length > 0) {
                const providerList = formatProviderList(providerLabels);
                return (
                    `${signInBaseMessage}: This email is already linked to ${providerList}. `
                    + `Please sign in with ${providerList}.`
                );
            }
        } catch {
            return getProviderSwitchFallbackMessage(type);
        }

        return getProviderSwitchFallbackMessage(type);
    }

    return getProviderAuthErrorMessage({
        type,
        error,
    });
};

export default getProviderAuthErrorMessage;
